/**
 * Hero Carousel JavaScript
 * Auto-rotating full-screen image carousel with smooth transitions
 * Supports click-to-navigate and automatic rotation
 */

class HeroCarousel {
  constructor(element) {
    this.carousel = element;
    this.track = this.carousel.querySelector('.hero-carousel__track');
    this.slides = [...this.carousel.querySelectorAll('.hero-carousel__slide')];
    this.indicators = [...this.carousel.querySelectorAll('.hero-carousel__indicator')];
    this.prevButton = this.carousel.querySelector('.hero-carousel__nav-button--prev');
    this.nextButton = this.carousel.querySelector('.hero-carousel__nav-button--next');
    this.progressBar = this.carousel.querySelector('.hero-carousel__progress-bar');
    
    // Configuration
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.autoRotate = this.carousel.dataset.autoRotate === 'true';
    this.rotationSpeed = parseInt(this.carousel.dataset.rotationSpeed) * 1000 || 5000;
    this.isTransitioning = false;
    
    // Single banner system for all devices
    this.bannersPerView = 1;
    
    // Auto-rotation timer
    this.rotationTimer = null;
    this.progressTimer = null;
    
    // Initialize if we have slides
    if (this.totalSlides > 0) {
      this.init();
    }
  }
  
  init() {
    this.setupEventListeners();
    this.updateResponsiveLayout();
    this.updateCarousel();
    
    if (this.autoRotate && this.totalSlides > 1) {
      this.startAutoRotation();
    }
    
    // Pause auto-rotation when page is not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoRotation();
      } else if (this.autoRotate && this.totalSlides > 1) {
        this.startAutoRotation();
      }
    });
    
    // Handle responsive layout changes
    window.addEventListener('resize', () => this.handleResize());
  }
  
  setupEventListeners() {
    // Navigation arrows
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.previousSlide());
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }
    
    // Indicator dots
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Keyboard navigation
    this.carousel.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.previousSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case ' ':
        case 'Enter':
          if (e.target.classList.contains('hero-carousel__indicator')) {
            e.preventDefault();
            const index = this.indicators.indexOf(e.target);
            this.goToSlide(index);
          }
          break;
      }
    });
    
    // Touch/swipe support
    this.setupTouchEvents();
    
    // No hover pause - auto-rotation continues always
    // Only pause on focus for accessibility
    this.carousel.addEventListener('focusin', () => this.pauseAutoRotation());
    this.carousel.addEventListener('focusout', () => {
      if (this.autoRotate && this.totalSlides > 1) {
        this.startAutoRotation();
      }
    });
    
    // Handle slide clicks for navigation
    this.slides.forEach((slide, index) => {
      const slideLink = slide.querySelector('.hero-carousel__slide-link');
      if (slideLink) {
        slideLink.addEventListener('click', (e) => {
          // Track click event for analytics if needed
          const categoryHandle = slide.dataset.category;
          if (categoryHandle) {
            this.trackSlideClick(categoryHandle, slideLink.href);
          }
        });
      }
    });
  }
  
  setupTouchEvents() {
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    
    // Touch Events (Mobile only - no mouse events)
    this.carousel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });
    
    this.carousel.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = startX - currentX;
      const diffY = startY - currentY;
      
      // Only handle horizontal swipes with minimum threshold
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        e.preventDefault();
        
        if (diffX > 0) {
          // Swiped left - go to next slide
          this.nextSlide();
        } else {
          // Swiped right - go to previous slide
          this.previousSlide();
        }
        
        isDragging = false;
      }
    }, { passive: false });
    
    this.carousel.addEventListener('touchend', () => {
      isDragging = false;
    }, { passive: true });
    
    // Prevent image drag
    this.carousel.addEventListener('dragstart', (e) => {
      e.preventDefault();
    });
  }
  
  nextSlide() {
    if (this.isTransitioning) return;
    
    // Single banner navigation for all devices
    const nextIndex = (this.currentSlide + 1) % this.totalSlides;
    this.goToSlide(nextIndex);
  }
  
  previousSlide() {
    if (this.isTransitioning) return;
    
    // Single banner navigation for all devices
    const prevIndex = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(prevIndex);
  }
  
  goToSlide(index) {
    if (this.isTransitioning || index === this.currentSlide) return;
    
    this.isTransitioning = true;
    this.pauseAutoRotation();
    
    // Single slide transition for all devices
    this.transitionToSlide(index);
  }
  
  transitionToSlide(index) {
    // Clear all active states and set new active slide
    this.slides[this.currentSlide].classList.remove('hero-carousel__slide--active');
    this.slides[this.currentSlide].classList.add('hero-carousel__slide--exiting');
    
    this.slides[index].classList.remove('hero-carousel__slide--exiting');
    this.slides[index].classList.add('hero-carousel__slide--entering');
    
    // Small delay to ensure CSS classes are applied
    setTimeout(() => {
      this.slides[index].classList.remove('hero-carousel__slide--entering');
      this.slides[index].classList.add('hero-carousel__slide--active');
      
      // Clean up previous slide
      setTimeout(() => {
        this.slides[this.currentSlide].classList.remove('hero-carousel__slide--exiting');
        this.currentSlide = index;
        this.updateCarousel();
        this.isTransitioning = false;
        
        // Restart auto-rotation if enabled
        if (this.autoRotate && this.totalSlides > 1) {
          this.startAutoRotation();
        }
      }, 800); // Match CSS transition duration
      
    }, 50);
  }
  
  updateCarousel() {
    // Update indicators - single banner behavior for all devices
    this.indicators.forEach((indicator, index) => {
      indicator.classList.toggle('hero-carousel__indicator--active', index === this.currentSlide);
      indicator.setAttribute('aria-pressed', index === this.currentSlide ? 'true' : 'false');
    });
    
    // Update ARIA attributes - single slide accessibility
    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index !== this.currentSlide ? 'true' : 'false');
    });
    
    // Update carousel aria-label
    this.carousel.setAttribute('aria-label', 
      `Image ${this.currentSlide + 1} of ${this.totalSlides}`
    );
  }
  
  updateResponsiveLayout() {
    // Initialize single slide layout for all devices
    this.slides.forEach(slide => {
      slide.classList.remove('hero-carousel__slide--active', 'hero-carousel__slide--active-pair', 
                              'hero-carousel__slide--entering', 'hero-carousel__slide--exiting');
    });
    
    // Set current active slide
    this.slides[this.currentSlide].classList.add('hero-carousel__slide--active');
  }
  
  handleResize() {
    // Update layout and carousel state on resize
    this.updateResponsiveLayout();
    this.updateCarousel();
    
    // Restart auto-rotation if needed
    if (this.autoRotate && this.totalSlides > 1) {
      this.startAutoRotation();
    }
  }
  
  startAutoRotation() {
    this.pauseAutoRotation(); // Clear any existing timers
    
    if (this.totalSlides <= 1) return;
    
    // Start progress bar animation if it exists
    if (this.progressBar) {
      this.progressBar.style.transition = `width ${this.rotationSpeed}ms linear`;
      this.progressBar.style.width = '100%';
    }
    
    this.rotationTimer = setTimeout(() => {
      this.nextSlide();
    }, this.rotationSpeed);
  }
  
  pauseAutoRotation() {
    if (this.rotationTimer) {
      clearTimeout(this.rotationTimer);
      this.rotationTimer = null;
    }
    
    if (this.progressTimer) {
      clearTimeout(this.progressTimer);
      this.progressTimer = null;
    }
    
    // Reset progress bar
    if (this.progressBar) {
      this.progressBar.style.transition = 'none';
      this.progressBar.style.width = '0%';
    }
  }
  
  trackSlideClick(categoryHandle, url) {
    // Analytics tracking for slide clicks
    if (typeof gtag !== 'undefined') {
      gtag('event', 'hero_carousel_click', {
        category: 'engagement',
        label: categoryHandle,
        value: this.currentSlide + 1
      });
    }
    
    // Custom event for other tracking systems
    const event = new CustomEvent('heroCarouselClick', {
      detail: {
        slideIndex: this.currentSlide,
        categoryHandle: categoryHandle,
        url: url,
        timestamp: Date.now()
      }
    });
    
    this.carousel.dispatchEvent(event);
  }
  
  // Public methods for external control
  destroy() {
    this.pauseAutoRotation();
    // Remove event listeners would go here if needed
  }
  
  play() {
    if (this.autoRotate && this.totalSlides > 1) {
      this.startAutoRotation();
    }
  }
  
  pause() {
    this.pauseAutoRotation();
  }
}

// Initialize carousel when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  const carousels = document.querySelectorAll('.hero-carousel');
  
  carousels.forEach(carousel => {
    new HeroCarousel(carousel);
  });
});

// Also initialize on theme section load (Shopify theme editor)
document.addEventListener('shopify:section:load', function(event) {
  if (event.target.classList.contains('section-hero-carousel')) {
    const carousel = event.target.querySelector('.hero-carousel');
    if (carousel) {
      new HeroCarousel(carousel);
    }
  }
});

// Handle theme editor section unload
document.addEventListener('shopify:section:unload', function(event) {
  if (event.target.classList.contains('section-hero-carousel')) {
    const carousel = event.target.querySelector('.hero-carousel');
    if (carousel && carousel.heroCarouselInstance) {
      carousel.heroCarouselInstance.destroy();
    }
  }
});

// Preload images for better performance
function preloadCarouselImages(carousel) {
  const slides = carousel.querySelectorAll('.hero-carousel__slide img[data-src]');
  
  slides.forEach((img, index) => {
    // Load first two images immediately, rest on demand
    if (index < 2) {
      const src = img.dataset.src;
      if (src) {
        img.src = src;
        img.removeAttribute('data-src');
      }
    }
  });
}

// Export for potential external use
if (typeof window !== 'undefined') {
  window.HeroCarousel = HeroCarousel;
}
